import { useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { Seo } from '@/components/common';
import { PreviewBanner } from '@/components/producer/PreviewBanner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useProducer } from '@/hooks/useProducer';
import { usePublicProducerEvents } from '@/hooks/usePublicProducerEvents';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { PublicEventCard } from '@/components/public/PublicEventCard';
import type { PublicProducerEvent } from '@/types/publicEvent';

const MAX_ACTIVITIES = 3;
const BASE_URL = 'https://www.farmly.se';

export default function ProducerBioPage() {
  const { slug } = useParams<{ slug: string }>();
  const { producer, loading, error, isPreview } = useProducer(slug || '');
  const { data: events, isLoading: eventsLoading } = usePublicProducerEvents(producer?.id);
  const heroCoverUrl = useCoverImageUrl('producer', producer?.id ?? null, 1280);

  const canonicalUrl = producer?.slug ? `${BASE_URL}/g/${producer.slug}` : undefined;
  const producerDetailUrl = producer
    ? `/producer/${producer.slug || producer.id}`
    : '/';

  const visibleEvents = (events || []).slice(0, MAX_ACTIVITIES);

  // OG description
  const ogDescription = producer
    ? producer.city
      ? `Upptäck ${producer.name} i ${producer.city} på Farmly.`
      : `Upptäck ${producer.name} på Farmly.`
    : undefined;

  // Loading state
  if (loading) {
    return (
      <>
        <SiteHeader density="compact" />
        <main className="container max-w-lg mx-auto py-12 space-y-6">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-5 w-1/2" />
          <div className="space-y-4 mt-8">
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </div>
        </main>
        <SiteFooter />
      </>
    );
  }

  // Error / not found
  if (error || !producer) {
    return (
      <>
        <SiteHeader density="compact" />
        <main className="container max-w-lg mx-auto py-16 text-center space-y-4">
          <h1 className="text-xl font-display text-foreground">
            Sidan kunde inte hittas
          </h1>
          <p className="text-sm text-muted-foreground">
            Producenten finns inte eller har inte publicerats ännu.
          </p>
          <Button variant="outline" asChild>
            <Link to="/">Till startsidan</Link>
          </Button>
        </main>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <Seo
        title={`${producer.name} på Farmly`}
        description={ogDescription}
        canonicalUrl={canonicalUrl}
        image={heroCoverUrl}
        imageAlt={`${producer.name} – bild`}
        ogType="website"
        noIndex={isPreview}
      />

      <SiteHeader density="compact" />

      {isPreview && (
        <PreviewBanner
          portalRoute={`/app/producer/farm/${producer.id}`}
        />
      )}

      <main className="container max-w-lg mx-auto py-8 px-4 space-y-8">
        {/* Hero — minimal, name-first */}
        <header className="space-y-2">
          <h1 className="font-display text-2xl text-foreground leading-tight">
            {producer.name}
          </h1>
          {producer.city && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              <span>{producer.city}</span>
              {producer.region && (
                <span className="text-muted-foreground/60">· {producer.region}</span>
              )}
            </div>
          )}
          {producer.short_description && (
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              {producer.short_description}
            </p>
          )}
        </header>

        {/* Activities — max 3 */}
        {eventsLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-28 rounded-xl" />
            <Skeleton className="h-28 rounded-xl" />
          </div>
        ) : visibleEvents.length > 0 ? (
          <section className="space-y-3">
            <h2 className="font-display text-base text-foreground">
              Det händer här
            </h2>
            <div className="grid gap-3">
              {visibleEvents.map((event) => (
                <BioEventCard key={event.event_id} event={event} producerCity={producer.city || undefined} fallbackCoverUrl={heroCoverUrl} />
              ))}
            </div>
          </section>
        ) : (
          <section className="rounded-xl border border-border/40 bg-muted/20 p-6 text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Inga kommande aktiviteter just nu.
            </p>
          </section>
        )}

        {/* Primary CTA */}
        <div className="pt-2">
          <Button asChild className="w-full gap-2">
            <Link to={producerDetailUrl}>
              Se på Farmly
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </main>

      <SiteFooter />
    </>
  );
}

/** Thin card wrapper for bio page events */
function BioEventCard({
  event,
  producerCity,
  fallbackCoverUrl,
}: {
  event: PublicProducerEvent;
  producerCity?: string;
  fallbackCoverUrl?: string | null;
}) {
  const coverUrl = useCoverImageUrl('event', event.event_id, 640);
  const timingMode = (event.timing_mode || 'date_time') as 'date_time' | 'open' | 'anytime' | 'season';

  return (
    <PublicEventCard
      event={{
        id: event.event_id,
        title: event.title,
        startDate: event.next_occurrence_at || event.start_date,
        endDate: event.end_date,
        timingMode,
        outingTags: event.outing_tags,
        producerName: null,
        producerCity,
        location: event.venue_text ?? event.location ?? null,
        eventType: (event.event_type as 'event' | 'course') || 'event',
        includesFarmSale: event.includes_farm_sale ?? false,
        includesFarmSaleAlcohol: event.includes_farm_sale_alcohol ?? false,
        occurrencesCount: event.occurrences_count ?? 0,
      }}
      coverImageUrl={coverUrl}
      fallbackCoverUrl={fallbackCoverUrl}
      onClick={() => {
        window.location.href = `/event/${event.event_id}`;
      }}
    />
  );
}
